/**
 * Download chips for the source files attached to an ingest run, rendered on
 * `batch_ingested` feed roots. Each chip streams the raw attachment bytes via
 * `/v1/batches/{batch_id}/attachments/{attachment_id}` and triggers a browser
 * download, mirroring the Kai chat download chips.
 */
import { useState } from "react";
import { Download, Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import type { BatchAttachmentRef } from "@/types/feed";

type AttachmentChipSize = "sm" | "md";

function AttachmentChip({
  attachment,
  size,
}: {
  attachment: BatchAttachmentRef;
  size: AttachmentChipSize;
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    setDownloading(true);
    try {
      const { data, filename } = await api.getAttachmentData(
        attachment.batch_id,
        attachment.attachment_id,
        attachment.filename,
      );
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn("Failed to download attachment:", err);
    } finally {
      setDownloading(false);
    }
  };

  const md = size === "md";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void handleClick();
      }}
      disabled={downloading}
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-primary/10 font-medium text-primary transition-colors hover:bg-primary/20",
        md ? "gap-1.5 px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]",
        downloading && "opacity-60",
      )}
    >
      {downloading ? (
        <Loader2 className={cn("animate-spin", md ? "size-3.5" : "size-2.5")} />
      ) : (
        <Download className={md ? "size-3.5" : "size-2.5"} />
      )}
      <span className={cn("truncate", md ? "max-w-[20rem]" : "max-w-[14rem]")}>
        {attachment.filename}
      </span>
    </button>
  );
}

export function FeedAttachmentDownloads({
  attachments,
  size = "sm",
}: {
  attachments: BatchAttachmentRef[];
  /** `md` enlarges the chip for standalone, prominent placements. */
  size?: AttachmentChipSize;
}) {
  if (attachments.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {attachments.map((att) => (
        <AttachmentChip key={att.attachment_id} attachment={att} size={size} />
      ))}
    </div>
  );
}
